import { Box, Center, Image, Text, VStack, Wrap, WrapItem, Grid, GridItem } from '@chakra-ui/react';
import DesktopIcon from './DesktopIcon';
import type { DesktopShellProps } from './types';
import type { WindowFlagName } from '../../types/window';

/**
 * Pure UI: desktop icons, taskbar, start menu, modes flyout.
 * All actions come from the app shell via props.
 */
export function DesktopShellInterface(props: DesktopShellProps) {
  const {
    currentTime,
    showMenu,
    showModesSubmenu,
    selectedDesktopIcon,
    gameMode,
    modesList,
    icons,
    refMenu,
    refMenuCard,
    refModesSubmenu,
    onToggleMenu,
    onToggleModesSubmenu,
    onSelectMode,
    onOpenMenuItem,
    onDesktopSelect,
    onDesktopOpen,
    modeMenuLabel,
  } = props;

  const openFromMenu = (flag: WindowFlagName) => {
    onOpenMenuItem(flag);
  };

  return (
    <>
      <div className="nav">
        <Wrap direction="column" paddingInline="12px" spacing="10px">
          {icons.map((icon) => (
            <WrapItem key={icon.id}>
              <DesktopIcon
                id={icon.id}
                label={icon.label}
                iconSrc={icon.iconSrc}
                selected={selectedDesktopIcon === icon.id}
                onSelect={onDesktopSelect}
                onOpen={onDesktopOpen}
              />
            </WrapItem>
          ))}
        </Wrap>
      </div>

      <Grid
        className="arcade-taskbar"
        templateColumns="repeat(22, 1fr)"
        w="100%"
        h="25px"
        bg="grey"
        border="1px solid black"
        color="black"
        position="fixed"
        bottom="0"
        zIndex={25}
      >
        <GridItem colSpan={1} minW="60px" ref={refMenu}>
          <Center w="100%" h="100%">
            <Box
              w="100%"
              h="100%"
              border="1px solid black"
              onClick={onToggleMenu}
              _hover={{
                color: '#ffffff',
              }}
              _active={{
                color: '#00bfa588',
              }}
            >
              <Center w="100%" h="100%">
                <Text fontSize="18px" fontFamily="BasedPixel" mt="0">
                  MENU
                </Text>
              </Center>
            </Box>
          </Center>
        </GridItem>
        <GridItem colSpan={20} fontFamily="BasedPixel" fontSize="16px">
          <marquee behavior="scroll" direction="left" scrollamount="6">
            ***** The owner is a really low functioning lazy individual until equiped with caffeine
            *****
          </marquee>
        </GridItem>
        <GridItem colSpan={1} minWidth="110px" alignItems="center" justifyContent="center">
          <Center w="100%" h="100%">
            <Box
              textAlign="center"
              fontFamily="BasedPixel"
              fontWeight="bold"
              fontSize="18px"
              w="100%"
              h="100%"
              border="1px solid black"
            >
              {currentTime.toLocaleTimeString([], { hour12: false })}
            </Box>
          </Center>
        </GridItem>
      </Grid>

      {showMenu ? (
        <>
          <Box
            className="arcade-start-menu"
            w="150px"
            h="360px"
            bg="gray"
            bottom="27px"
            left="0"
            position="fixed"
            border="1px solid black"
            borderBottom="0px"
            borderLeft="2px solid black"
            ref={refMenuCard}
            zIndex={30}
          >
            <Grid
              templateColumns="repeat(6, 1fr)"
              templateRows="1fr 1fr 1fr 1fr 40px 40px"
              h="100%"
              w="100%"
            >
              <GridItem
                colSpan={1}
                rowSpan={6}
                h="100%"
                w="100%"
                bg="linear-gradient(0deg, #009688, gray)"
              >
                <Center w="100%" h="100%">
                  <Text
                    sx={{ writingMode: 'vertical-rl' }}
                    transform="rotate(180deg)"
                    fontSize="16px"
                    fontFamily="BasedPixel"
                    color="white"
                    position="absolute"
                    bottom="10"
                    letterSpacing="-2%"
                  >
                    THYME
                  </Text>
                  <Text
                    sx={{ writingMode: 'vertical-rl' }}
                    transform="rotate(180deg)"
                    fontSize="13px"
                    fontFamily="BasedPixel"
                    color="white"
                    position="absolute"
                    bottom="60"
                    letterSpacing="-5%"
                  >
                    PERFORMANCE v2.0
                  </Text>
                </Center>
              </GridItem>
              <GridItem colSpan={5} rowSpan={4}>
                <VStack mt="8px" spacing={0}>
                  {(
                    [
                      {
                        flag: 'showHideSkills' as const,
                        label: 'ABOUT',
                        icon: icons.find((i) => i.id === 'skills')?.iconSrc ?? '',
                        hover: '#00968880',
                      },
                      {
                        flag: 'showHideProjects' as const,
                        label: 'PROJECTS',
                        icon: icons.find((i) => i.id === 'projects')?.iconSrc ?? '',
                        hover: '#00BFA580',
                      },
                      {
                        flag: 'showHideAbout' as const,
                        label: 'CONTACT',
                        icon: icons.find((i) => i.id === 'about')?.iconSrc ?? '',
                        hover: '#00968880',
                      },
                      {
                        flag: 'showHideBlogs' as const,
                        label: 'BLOGS',
                        icon: icons.find((i) => i.id === 'blogs')?.iconSrc ?? '',
                        hover: '#00968880',
                      },
                    ] as const
                  ).map((item) => (
                    <Box
                      key={item.flag}
                      w="100%"
                      h="40px"
                      display="flex"
                      _hover={{
                        bg: item.hover,
                        color: 'white',
                      }}
                      onClick={() => openFromMenu(item.flag)}
                    >
                      <Box w="40px" h="40px">
                        <Center h="100%" w="100%">
                          <Image w="40px" h="40px" src={item.icon} />
                        </Center>
                      </Box>
                      <Box w="calc(100%-40px)" h="40px">
                        <Center w="100%" h="100%">
                          <Text paddingInline="5px" fontSize="16px" fontFamily="BasedPixel">
                            {item.label}
                          </Text>
                        </Center>
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </GridItem>
              <GridItem colSpan={5} borderTop="1px solid black">
                <Box
                  w="100%"
                  h="40px"
                  display="flex"
                  alignItems="center"
                  bg={showModesSubmenu ? '#00968855' : undefined}
                  _hover={{
                    bg: '#00968880',
                    color: 'white',
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleModesSubmenu();
                  }}
                >
                  <Box flex="1" h="40px">
                    <Center h="100%" justifyContent="flex-start" pl="8px">
                      <Text fontSize="16px" fontFamily="BasedPixel">
                        MODES
                      </Text>
                    </Center>
                  </Box>
                  <Box w="28px" h="40px">
                    <Center h="100%">
                      <Text fontSize="18px" fontFamily="BasedPixel" fontWeight="bold">
                        ›
                      </Text>
                    </Center>
                  </Box>
                </Box>
              </GridItem>
              <GridItem colSpan={5} borderTop="1px solid black">
                <Box
                  w="100%"
                  h="40px"
                  display="flex"
                  _hover={{
                    bg: '#00968880',
                    color: 'white',
                  }}
                  onClick={() => openFromMenu('showHideCredits')}
                >
                  <Box w="calc(100%-40px)" h="40px">
                    <Center h="100%">
                      <Text paddingInline="5px" fontSize="16px" fontFamily="BasedPixel">
                        CREDITS
                      </Text>
                    </Center>
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          </Box>
          {showModesSubmenu ? (
            <Box
              className="arcade-modes-submenu"
              ref={refModesSubmenu}
              w="150px"
              bg="gray"
              bottom="27px"
              left="150px"
              position="fixed"
              border="1px solid black"
              borderBottom="0px"
              borderLeft="0px"
              zIndex={31}
              py="4px"
            >
              {modesList.map((m) => (
                <Box
                  key={m}
                  w="100%"
                  h="40px"
                  display="flex"
                  alignItems="center"
                  px="10px"
                  bg={gameMode === m ? '#00968880' : undefined}
                  color={gameMode === m ? 'white' : undefined}
                  _hover={{ bg: '#00968880', color: 'white' }}
                  onClick={() => onSelectMode(m)}
                >
                  <Text fontSize="15px" fontFamily="BasedPixel" flex="1">
                    {modeMenuLabel(m)}
                  </Text>
                  {gameMode === m ? (
                    <Text fontSize="14px" fontFamily="BasedPixel">
                      ✓
                    </Text>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}
        </>
      ) : null}
    </>
  );
}
